<template>
  <section
    v-if="slides.length"
    v-bind="{ id }"
    aria-roledescription="carousel"
    class="trust-teaser-carousel"
    role="region"
  >
    <header class="flex items-center justify-between px-100 mb-100">
      <slot name="headline" />

      <nav class="flex gap-100">
        <LcButton
          :aria-controls="`${id}-carousel`"
          :aria-label="$t('common.carousel.button.prev')"
          icon="chevron-left"
          size="small"
          variant="filled"
          @click="() => carouselRef?.prev()"
        />

        <LcButton
          :aria-controls="`${id}-carousel`"
          :aria-label="$t('common.carousel.button.next')"
          icon="chevron-right"
          size="small"
          variant="filled"
          @click="() => carouselRef?.next()"
        />
      </nav>
    </header>

    <LcCarousel
      :id="`${id}-carousel`"
      ref="carousel"
      class="carousel"
      :key-extractor="getKey"
      loop
      skip-snaps
      :slides="preparedSlides"
    >
      <template #slide="{ attributes, index, slide }">
        <Component
          :is="getSlideComponent(slide)"
          v-bind="{
            ...attributes,
            ...slide,
            index,
          }"
          :aria-label="
            $t('common.carousel.slide.arialabel', {
              slide: index + 1,
              totalSlides: preparedSlides.length,
            })
          "
        />
      </template>
    </LcCarousel>
  </section>
</template>

<script lang="ts" setup>
import type { TrustTeaserCardAppReviewProps } from '~/components/TrustTeaser/TrustTeaserCardAppReview.vue'
import type { TrustTeaserCardWinnerProps } from '~/components/TrustTeaser/TrustTeaserCardWinner.vue'

import { TrustTeaserCardAppReview, TrustTeaserCardWinner } from '#components'

type TrustTeaserCarouselProps = {
  slides: TrustTeaserCarouselSlide[]
}

type TrustTeaserCarouselSlide =
  | Omit<TrustTeaserCardWinnerProps, 'index'>
  | TrustTeaserCardAppReviewProps

const props = defineProps<TrustTeaserCarouselProps>()

const carouselRef = useTemplateRef('carousel')
const id = useId()

const preparedSlides = computed(() => {
  // Fill array with duplicates to avoid empty slides in loop
  if (props.slides.length < 4) {
    return Array.from({ length: 4 }, (_, index) => props.slides[index % props.slides.length])
  }

  return props.slides
})

const getKey = (slide: TrustTeaserCarouselSlide, index: number) => {
  if (isAppReview(slide)) return slide.username
  if (isWinner(slide)) {
    return `${slide.state}-${slide.game}-${slide.winningAmount}`
  }
  return String(index)
}

const getSlideComponent = (slide: TrustTeaserCarouselSlide) => {
  if (isAppReview(slide)) return TrustTeaserCardAppReview
  if (isWinner(slide)) return TrustTeaserCardWinner
}

const isAppReview = (slide: TrustTeaserCarouselSlide): slide is TrustTeaserCardAppReviewProps =>
  typeof (slide as TrustTeaserCardAppReviewProps).rating === 'number'

const isWinner = (slide: TrustTeaserCarouselSlide): slide is TrustTeaserCardWinnerProps =>
  typeof (slide as TrustTeaserCardWinnerProps).winningAmount === 'number'
</script>

<style lang="scss" scoped>
.trust-teaser-carousel {
  overflow: hidden;

  nav {
    display: none;
    padding-top: 0.5rem;

    @include media-desktop {
      display: flex;
    }
  }

  .carousel {
    padding: 0 0.5rem;

    /* stylelint-disable-next-line selector-class-pattern */
    :deep(.lc-carousel__container) {
      overflow: visible;

      @include media-desktop {
        position: relative;
        overflow: hidden;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          z-index: 10;
          width: 4rem;
          height: 100%;
          background: linear-gradient(90deg, rgba(#eef9ff, 0%) 5%, rgba(#eef9ff, 100%) 90%);
        }
      }
    }
  }
}
</style>
